/** Tree planting is world geometry; appearance packs only change its finish. */
import type { DecorDef } from "./mission";
import type { RoadNetwork } from "./network";
import type { Obstacle } from "./types";

export const TREE_TRUNK_RADIUS_M = 0.2;
export const TREE_SPACING_M = 9;
export const TREE_VERGE_WIDTH_M = 1.1;
export const TREE_KERB_WIDTH_M = 0.14;

export interface StreetTreePlacement {
  x: number;
  z: number;
  pieceId: string;
  segment: number;
}

/** Shared by rendering and collision queries, including junction-mouth gaps. */
export function streetTreePlacements(network: RoadNetwork, mirror: 1 | -1 = 1): StreetTreePlacement[] {
  const trees: StreetTreePlacement[] = [];
  for (const piece of network.pieces) {
    if (piece.kind !== "band" || piece.roadType !== "street") continue;
    let planted = 0;
    for (let segment = 0; segment + 1 < piece.pts.length; segment++) {
      const a = piece.pts[segment]!;
      const b = piece.pts[segment + 1]!;
      const len = Math.hypot(b.x - a.x, b.z - a.z);
      if (len < 1e-6) continue;
      const ux = (b.x - a.x) / len;
      const uz = (b.z - a.z) / len;
      for (let d = TREE_SPACING_M / 2; d < len; d += TREE_SPACING_M) {
        planted++;
        const side = (planted % 2 === 0 ? 1 : -1) * mirror;
        const offset = piece.halfWidth + TREE_KERB_WIDTH_M + TREE_VERGE_WIDTH_M / 2;
        const x = a.x + ux * d + uz * side * offset;
        const z = a.z + uz * d - ux * side * offset;
        if (network.sdf(x, z) >= 0.3) trees.push({ x, z, pieceId: piece.id, segment });
      }
    }
  }
  return trees;
}

/** Solid trunk footprints; crowns, soil pits and shadows remain passable. */
export function treeObstacles(network: RoadNetwork, decor: readonly DecorDef[], mirror: 1 | -1 = 1): Obstacle[] {
  const positions = [
    ...decor.filter((d) => d.kind === "tree"),
    ...streetTreePlacements(network, mirror),
    ...network.pieces.flatMap((p) => p.kind === "ring" ? [{ x: p.cx, z: p.cz }] : []),
  ];
  const seen = new Set<string>();
  return positions.flatMap(({ x, z }) => {
    const key = `${x},${z}`;
    if (seen.has(key)) return [];
    seen.add(key);
    return [{ kind: "tree", x, z, halfW: TREE_TRUNK_RADIUS_M, halfD: TREE_TRUNK_RADIUS_M, height: 3 }];
  });
}
